import Task from '../types/Task';
import { TaskType } from '../types/TaskType';

/**
 * The single definition of "a subitem you can see under this parent", shared by the
 * task view (which reads children from the server) and the tables (which nest them
 * from the space's task cache). The two used to apply different rules, so the same
 * parent reported different counts in the two places. (#2884)
 *
 * `GET /tasks/:id/subtasks` serves two different relations: a normal parent is
 * matched on parentTaskId, but an epic parent is matched on epicId with
 * parentTaskId: null (server: getEpicSubtasks). Mirror that split here, or every
 * epic reports zero children. (#2884)
 *
 * A child's archived state must match its parent's — compared via `stage`, not
 * `archivedAt`. The server only ever SETs archivedAt when archiving and never
 * clears it on un-archive (preserves audit history), so a task restored from
 * Archive keeps a non-null archivedAt while sitting in Backlog. Keying on
 * archivedAt would treat any once-archived task as archived forever and drop its
 * subitems the moment its parent was restored; `stage` is the signal the rest of
 * the app already uses for archive membership. (#2884)
 */
type SubitemParent = { id: number; workspaceId: number; type: string; stage: string };

// The rule itself. Everything below routes through this so the indexed form below can never
// drift from the plain one — the index only narrows *which* children get tested, never how.
const isVisibleSubitem = (child: Task, parent: SubitemParent): boolean => {
  const belongsToParent =
    parent.type === TaskType.epic
      ? child.epicId === parent.id && child.parentTaskId == null && child.type !== TaskType.epic
      : child.parentTaskId === parent.id;

  return (
    belongsToParent &&
    child.workspaceId === parent.workspaceId &&
    child.stage !== 'triage' &&
    (child.stage === 'archive') === (parent.stage === 'archive')
  );
};

export const selectVisibleSubitems = (children: Task[], parent: SubitemParent): Task[] =>
  (children ?? []).filter((child) => isVisibleSubitem(child, parent));

/**
 * Bulk form of `selectVisibleSubitems`: bucket the children once, then serve every parent
 * from its own bucket. Nesting a whole table used to call `selectVisibleSubitems` per row,
 * and each call re-scanned the entire space list — O(rows × tasks). On a few thousand tasks
 * that ran on every refetch of the list and blocked the main thread for hundreds of ms. (#2803)
 *
 * The buckets key on exactly what `belongsToParent` keys on, so a bucket is a superset of the
 * matches and the real predicate still decides each one — same output, fewer comparisons.
 */
export const buildVisibleSubitemIndex = (children: Task[]) => {
  const byParentId = new Map<number, Task[]>();
  const byEpicId = new Map<number, Task[]>();

  for (const child of children ?? []) {
    // A child with a parent can only ever match that parent; only parentless children are
    // reachable as an epic's direct subitems (`child.parentTaskId == null` above).
    const bucket = child.parentTaskId != null ? byParentId : byEpicId;
    const key = child.parentTaskId != null ? child.parentTaskId : child.epicId;
    if (key == null) continue;
    const existing = bucket.get(key);
    if (existing) existing.push(child);
    else bucket.set(key, [child]);
  }

  return (parent: SubitemParent): Task[] => {
    const candidates = (parent.type === TaskType.epic ? byEpicId.get(parent.id) : byParentId.get(parent.id)) ?? [];
    return candidates.filter((child) => isVisibleSubitem(child, parent));
  };
};
